import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { Loader2, AlertCircle } from "lucide-react";
import logoXero from "@/assets/logo-xero.png";
import {
  useGetXeroStatus,
  usePushInvoiceToXero,
  usePushEstimateToXero,
  useGetXeroInvoiceSyncStatus,
  useGetXeroEstimateSyncStatus,
} from "@workspace/api-client-react";

type Props =
  | { kind: "invoice"; id: number }
  | { kind: "estimate"; id: number };

function formatRelative(iso: string): string {
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}

export function XeroPushButton(props: Props) {
  const { toast } = useToast();
  const { data: status } = useGetXeroStatus();

  const isInvoice = props.kind === "invoice";

  const pushInvoice = usePushInvoiceToXero();
  const pushEstimate = usePushEstimateToXero();
  const invoiceSync = useGetXeroInvoiceSyncStatus(isInvoice ? props.id : undefined, {
    enabled: isInvoice && status?.connected === true,
  });
  const estimateSync = useGetXeroEstimateSyncStatus(!isInvoice ? props.id : undefined, {
    enabled: !isInvoice && status?.connected === true,
  });

  if (!status?.connected) return null;

  const needsReconnect = (status as { needsReconnect?: boolean }).needsReconnect === true;
  const syncData = isInvoice ? invoiceSync.data : estimateSync.data;
  const isPending = isInvoice ? pushInvoice.isPending : pushEstimate.isPending;

  const hasPushed = !!syncData?.externalId || !!syncData?.lastSuccess;
  const lastSuccessAt = syncData?.lastSuccess?.createdAt ?? null;
  const lastFailed = syncData?.entry?.status === "failure"
    && (!syncData.lastSuccess || new Date(syncData.entry.createdAt) > new Date(syncData.lastSuccess.createdAt));

  const noun = isInvoice ? "invoice" : "quote";

  const onClick = async () => {
    try {
      if (isInvoice) {
        await pushInvoice.mutateAsync(props.id);
      } else {
        await pushEstimate.mutateAsync(props.id);
      }
      toast({
        title: hasPushed ? "Updated in Xero" : "Pushed to Xero",
        description: isInvoice ? "Invoice synced." : "Quote synced.",
      });
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      toast({
        title: "Xero push failed",
        description: e?.response?.data?.error || e?.message || "Unknown error",
        variant: "destructive",
      });
    }
  };

  const label = hasPushed
    ? `Update ${noun} in Xero`
    : `Push ${noun} to Xero`;

  const tooltipText = needsReconnect
    ? "Reconnect Xero in Integrations to push."
    : lastSuccessAt
      ? `Last pushed ${formatRelative(lastSuccessAt)}${lastFailed ? " · last attempt failed" : ""}`
      : hasPushed
        ? "Send your latest changes to Xero."
        : `Create this ${noun} in Xero.`;

  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <Button
          size="icon"
          className="h-10 w-10"
          variant="outline"
          onClick={onClick}
          disabled={isPending || needsReconnect}
          aria-label={label}
          data-testid={`button-xero-push-${props.kind}`}
        >
          {isPending ? (
            <Loader2 className="w-5 h-5 animate-spin" strokeWidth={2.25} />
          ) : lastFailed ? (
            <AlertCircle className="w-5 h-5 text-amber-600" strokeWidth={2.25} />
          ) : hasPushed ? (
            <span className="relative inline-flex">
              <img src={logoXero} alt="Xero" className="w-5 h-5 object-contain" />
              <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-green-500 border border-background" />
            </span>
          ) : (
            <img src={logoXero} alt="Xero" className="w-5 h-5 object-contain" />
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{tooltipText}</TooltipContent>
    </Tooltip>
  );
}
